// ============================================
// Message Item Component
// Individual message bubble with reactions
// ============================================

import React, { useState, useRef, useEffect } from 'react';
import { useChat } from './ChatProvider';
import PresenceIndicator from './PresenceIndicator';

// Common emoji reactions
const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🎉'];

/**
 * Single message display with sender info and reactions
 */
const MessageItem = ({ message, showSender, isOwnMessage, isAdmin, isHighlighted, isDM, onReaction, onStartDM, onEdit, onDelete, onReply, onViewThread, threadReplies, onPin, onUnpin, onReport }) => {
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(message.content);
  const [isSaving, setIsSaving] = useState(false);
  const [showThread, setShowThread] = useState(false);
  const [isPinning, setIsPinning] = useState(false);
  const editInputRef = useRef(null);

  // Get presence status from chat context
  const { getUserPresenceStatus } = useChat();

  // Handle pin/unpin
  const handlePinToggle = async () => {
    if (isPinning) return;
    setIsPinning(true);
    try {
      if (message.is_pinned) {
        if (onUnpin) await onUnpin(message.id);
      } else {
        if (onPin) await onPin(message.id);
      }
    } finally {
      setIsPinning(false);
      setShowActions(false);
    }
  };

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.setSelectionRange(editContent.length, editContent.length);
    }
  }, [isEditing]);

  // Format timestamp
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Check if avatar URL is valid (not empty, not placeholder, starts with http)
  const isValidAvatarUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    const trimmed = url.trim();
    if (!trimmed || trimmed === '0' || trimmed === 'null' || trimmed === 'undefined') return false;
    return trimmed.startsWith('http://') || trimmed.startsWith('https://');
  };

  // Get background color based on user type
  const getAvatarColor = (userType) => {
    switch (userType) {
      case 'student':
        return 'bg-blue-500';
      case 'recruiter':
        return 'bg-green-500';
      case 'alumni':
        return 'bg-purple-500';
      case 'admin':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  // Get role badge
  const getRoleBadge = (userType) => {
    switch (userType) {
      case 'recruiter':
        return (
          <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-medium bg-green-100 text-green-700 rounded">
            Recruiter
          </span>
        );
      case 'alumni':
        return (
          <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-medium bg-purple-100 text-purple-700 rounded">
            Alumni
          </span>
        );
      case 'admin':
        return (
          <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-medium bg-red-100 text-red-700 rounded">
            Admin
          </span>
        );
      default:
        return null;
    }
  };

  // Convert URLs and @mentions in text to styled elements
  const renderMessageContent = (content) => {
    if (!content) return null;

    // Combined pattern for URLs and @mentions
    // @mentions: @followed by non-whitespace characters
    const combinedPattern = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])|(@[\w\s]+?)(?=\s|$|@)/g;

    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = combinedPattern.exec(content)) !== null) {
      // Add text before match
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: content.substring(lastIndex, match.index)
        });
      }

      // Determine match type
      if (match[1]) {
        // URL match
        parts.push({
          type: 'url',
          content: match[1]
        });
      } else if (match[2]) {
        // @mention match
        parts.push({
          type: 'mention',
          content: match[2]
        });
      }

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < content.length) {
      parts.push({
        type: 'text',
        content: content.substring(lastIndex)
      });
    }

    // If no matches found, return plain text
    if (parts.length === 0) {
      return content;
    }

    return parts.map((part, index) => {
      if (part.type === 'url') {
        return (
          <a
            key={index}
            href={part.content}
            target="_blank"
            rel="noopener noreferrer"
            className={`underline break-all ${isOwnMessage ? 'text-blue-100 hover:text-white' : 'text-blue-600 hover:text-blue-800'}`}
          >
            {part.content}
          </a>
        );
      } else if (part.type === 'mention') {
        return (
          <span
            key={index}
            className={`font-medium ${isOwnMessage ? 'text-blue-100 bg-blue-500' : 'text-blue-600 bg-blue-100'} px-1 rounded`}
          >
            {part.content}
          </span>
        );
      }
      return part.content;
    });
  };

  const sender = message.sender || {};
  const reactions = message.reactions || {};
  const isDeleted = message.is_deleted;

  const handleReaction = (emoji) => {
    if (onReaction) {
      onReaction(message.id, emoji);
    }
    setShowReactionPicker(false);
  };

  const handleStartEdit = () => {
    setEditContent(message.content);
    setIsEditing(true);
    setShowActions(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditContent(message.content);
  };

  const handleSaveEdit = async () => {
    if (!onEdit || editContent.trim() === message.content) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    const success = await onEdit(message.id, editContent.trim());
    setIsSaving(false);

    if (success) {
      setIsEditing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    const confirmMessage = isOwnMessage
      ? 'Delete this message? This cannot be undone.'
      : 'Remove this message as moderator? The message will show as "[Message removed by moderator]".';

    if (window.confirm(confirmMessage)) {
      setShowActions(false);
      await onDelete(message.id);
    }
  };

  return (
    <div
      id={`message-${message.id}`}
      className={`
        mb-1 ${showSender ? 'mt-3' : 'mt-0.5'} group relative
        ${isHighlighted ? 'animate-pulse ring-2 ring-blue-400 ring-offset-2 rounded-lg bg-blue-50' : ''}
      `}
      onMouseLeave={() => {
        setShowReactionPicker(false);
        setShowActions(false);
      }}
    >
      <div className={`flex ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'} ${showSender ? 'items-start' : 'items-end'} space-x-2 ${isOwnMessage ? 'space-x-reverse' : ''}`}>
        {/* Avatar - only show for first message in group */}
        {showSender ? (
          <div className="flex-shrink-0 relative">
            {!isOwnMessage && onStartDM ? (
              // Clickable avatar for other users' messages
              <button
                onClick={() => onStartDM(sender.user_type, sender.user_id, sender.display_name)}
                className="focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-full hover:opacity-80 transition-opacity relative"
                title={`Message ${sender.display_name}`}
              >
                {isValidAvatarUrl(sender.avatar_url) ? (
                  <img
                    src={sender.avatar_url}
                    alt={sender.display_name}
                    className="w-8 h-8 rounded-full object-cover cursor-pointer"
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
                  />
                ) : null}
                <div className={`w-8 h-8 rounded-full ${getAvatarColor(sender.user_type)} flex items-center justify-center text-white text-xs font-medium cursor-pointer ${isValidAvatarUrl(sender.avatar_url) ? 'hidden' : ''}`}>
                  {getInitials(sender.display_name)}
                </div>
                {/* Presence indicator */}
                <span className="absolute -bottom-0.5 -right-0.5">
                  <PresenceIndicator
                    status={getUserPresenceStatus(sender.user_type, sender.user_id)}
                    size="sm"
                    showBorder
                  />
                </span>
              </button>
            ) : (
              // Non-clickable avatar for own messages
              <div className="relative">
                {isValidAvatarUrl(sender.avatar_url) ? (
                  <img
                    src={sender.avatar_url}
                    alt={sender.display_name}
                    className="w-8 h-8 rounded-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
                  />
                ) : null}
                <div className={`w-8 h-8 rounded-full ${getAvatarColor(sender.user_type)} flex items-center justify-center text-white text-xs font-medium ${isValidAvatarUrl(sender.avatar_url) ? 'hidden' : ''}`}>
                  {getInitials(sender.display_name)}
                </div>
                {/* Presence indicator for own messages */}
                <span className="absolute -bottom-0.5 -right-0.5">
                  <PresenceIndicator
                    status={getUserPresenceStatus(sender.user_type, sender.user_id)}
                    size="sm"
                    showBorder
                  />
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="w-8 flex-shrink-0"></div>
        )}

        {/* Message content */}
        <div className={`flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'} max-w-[75%]`}>
          {/* Sender name - only show for first message in group */}
          {showSender && (
            <div className={`flex items-center mb-0.5 ${isOwnMessage ? 'flex-row-reverse' : ''}`}>
              <span className="text-xs font-medium text-gray-700">
                {sender.display_name || 'Unknown'}
              </span>
              {getRoleBadge(sender.user_type)}
              {/* Pinned indicator */}
              {message.is_pinned && (
                <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700 rounded flex items-center space-x-0.5" title="Pinned message">
                  <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L10 6.917V16h3a1 1 0 110 2H7a1 1 0 110-2h3V6.917L6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z" />
                  </svg>
                  <span>Pinned</span>
                </span>
              )}
            </div>
          )}

          {/* Message bubble */}
          {isEditing ? (
            // Edit mode
            <div className="w-full">
              <textarea
                ref={editInputRef}
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 text-sm border border-blue-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                rows={Math.min(5, editContent.split('\n').length + 1)}
                disabled={isSaving}
              />
              <div className="flex items-center space-x-2 mt-1">
                <button
                  onClick={handleSaveEdit}
                  disabled={isSaving || !editContent.trim()}
                  className="px-2 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50"
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                  className="px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <span className="text-[10px] text-gray-400">
                  Enter to save, Esc to cancel
                </span>
              </div>
            </div>
          ) : (
            // Normal display mode
            <div
              className={`
                px-3 py-2 rounded-2xl max-w-full break-words relative
                ${isDeleted
                  ? 'bg-gray-100 text-gray-400 italic'
                  : isOwnMessage
                    ? 'bg-blue-600 text-white rounded-br-md'
                    : 'bg-gray-100 text-gray-800 rounded-bl-md'
                }
              `}
            >
              <p className="text-sm whitespace-pre-wrap">
                {isDeleted ? message.content : renderMessageContent(message.content)}
              </p>

              {/* Attachments */}
              {!isDeleted && message.attachments && message.attachments.length > 0 && (
                <div className="mt-2 space-y-2">
                  {message.attachments.map((attachment, index) => {
                    const isImage = attachment.file_type === 'image' ||
                      (attachment.content_type && attachment.content_type.startsWith('image/')) ||
                      (attachment.filename && /\.(jpg|jpeg|png|gif|webp)$/i.test(attachment.filename));

                    if (isImage) {
                      // Display image inline
                      return (
                        <a
                          key={index}
                          href={attachment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <img
                            src={attachment.url}
                            alt={attachment.filename || 'Image'}
                            className="max-w-full max-h-60 rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                            loading="lazy"
                          />
                        </a>
                      );
                    } else {
                      // Display document with download link
                      const formatSize = (bytes) => {
                        if (!bytes) return '';
                        if (bytes < 1024) return `${bytes} B`;
                        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
                        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
                      };

                      return (
                        <a
                          key={index}
                          href={attachment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          download={attachment.filename}
                          className={`flex items-center space-x-2 px-3 py-2 rounded-lg ${
                            isOwnMessage
                              ? 'bg-blue-500 hover:bg-blue-400'
                              : 'bg-gray-200 hover:bg-gray-300'
                          } transition-colors`}
                        >
                          {/* Document icon */}
                          <svg className={`w-8 h-8 flex-shrink-0 ${isOwnMessage ? 'text-blue-100' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${isOwnMessage ? 'text-white' : 'text-gray-800'}`}>
                              {attachment.filename || 'Document'}
                            </p>
                            {attachment.size && (
                              <p className={`text-xs ${isOwnMessage ? 'text-blue-100' : 'text-gray-500'}`}>
                                {formatSize(attachment.size)}
                              </p>
                            )}
                          </div>
                          {/* Download icon */}
                          <svg className={`w-5 h-5 flex-shrink-0 ${isOwnMessage ? 'text-blue-100' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </a>
                      );
                    }
                  })}
                </div>
              )}
            </div>
          )}

          {/* Action buttons - only show for messages that aren't deleted */}
          {!isEditing && !isDeleted && (
            <div className={`flex items-center space-x-1 mt-0.5 ${isOwnMessage ? 'flex-row-reverse space-x-reverse' : ''}`}>
              {/* Reaction button */}
              <button
                onClick={() => setShowReactionPicker(!showReactionPicker)}
                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded transition-opacity"
                title="Add reaction"
              >
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>

              {/* Reply button */}
              {onReply && !message.parent_message_id && (
                <button
                  onClick={() => onReply(message)}
                  className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded transition-opacity"
                  title="Reply in thread"
                >
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                </button>
              )}

              {/* Pin/Unpin button - available to all channel members */}
              {(onPin || onUnpin) && (
                <button
                  onClick={handlePinToggle}
                  disabled={isPinning}
                  className={`opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded transition-opacity ${message.is_pinned ? 'text-amber-500' : 'text-gray-400'}`}
                  title={message.is_pinned ? 'Unpin message' : 'Pin message'}
                >
                  {isPinning ? (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill={message.is_pinned ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                    </svg>
                  )}
                </button>
              )}

              {/* Edit/Delete/Report buttons */}
              {((isOwnMessage || isAdmin) && onDelete) || (!isOwnMessage && onReport) ? (
                <div className="relative">
                  <button
                    onClick={() => setShowActions(!showActions)}
                    className={`opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded transition-opacity ${!isOwnMessage && isAdmin ? 'text-red-400' : ''}`}
                    title={isOwnMessage ? "More actions" : (isAdmin ? "Moderate message" : "More options")}
                  >
                    {!isOwnMessage && isAdmin ? (
                      // Shield icon for admin moderation
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    ) : (
                      // Three dots for message actions
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                      </svg>
                    )}
                  </button>

                  {/* Actions dropdown */}
                  {showActions && (
                    <div className={`absolute ${isOwnMessage ? 'right-0' : 'left-0'} mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-20 min-w-[120px]`}>
                      {/* Edit button - only for own messages, commented out for now
                      {isOwnMessage && onEdit && (
                        <button
                          onClick={handleStartEdit}
                          className="w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          <span>Edit</span>
                        </button>
                      )}
                      */}

                      {/* Report button - only for other users' messages */}
                      {!isOwnMessage && onReport && (
                        <button
                          onClick={() => {
                            setShowActions(false);
                            onReport(message);
                          }}
                          className="w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                          </svg>
                          <span>Report</span>
                        </button>
                      )}

                      {/* Delete/Remove button */}
                      {(isOwnMessage || isAdmin) && onDelete && (
                        <button
                          onClick={handleDelete}
                          className="w-full px-3 py-1.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          <span>{isOwnMessage ? 'Delete' : 'Remove'}</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          )}

          {/* Emoji picker dropdown */}
          {showReactionPicker && (
            <div className={`absolute ${isOwnMessage ? 'right-0' : 'left-10'} mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-10`}>
              <div className="flex space-x-1">
                {QUICK_REACTIONS.map(emoji => (
                  <button
                    key={emoji}
                    onClick={() => handleReaction(emoji)}
                    className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded transition-colors text-lg"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Timestamp, edited indicator, and read receipt */}
          {!isEditing && (
            <div className={`flex items-center mt-0.5 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
              <span className="text-[10px] text-gray-400 flex items-center space-x-1">
                <span>{formatTime(message.created_at)}</span>
                {message.is_edited && !isDeleted && (
                  <span>(edited)</span>
                )}
                {/* Read receipt for own messages in DMs */}
                {isDM && isOwnMessage && !isDeleted && (
                  <span className={message.read_at ? 'text-blue-500' : 'text-gray-400'} title={message.read_at ? `Read ${formatTime(message.read_at)}` : 'Sent'}>
                    {message.read_at ? (
                      // Double checkmark for read
                      <svg className="w-3.5 h-3.5 inline" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M2 12l5 5L18 6" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M7 12l5 5L23 6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : (
                      // Single checkmark for sent
                      <svg className="w-3.5 h-3.5 inline" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </span>
                )}
              </span>
            </div>
          )}

          {/* Reactions */}
          {!isEditing && Object.keys(reactions).length > 0 && (
            <div className={`flex flex-wrap gap-1 mt-1 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
              {Object.entries(reactions).map(([emoji, users]) => (
                <button
                  key={emoji}
                  className="flex items-center space-x-1 px-2 py-0.5 bg-gray-100 hover:bg-gray-200 rounded-full text-xs transition-colors"
                  title={users.map(u => u.display_name || `${u.user_type}:${u.user_id}`).join(', ')}
                >
                  <span>{emoji}</span>
                  <span className="text-gray-600">
                    {users.length === 1 && users[0].display_name
                      ? users[0].display_name.split(' ')[0]
                      : users.length}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Thread reply count */}
          {!isEditing && !isDeleted && message.thread_reply_count > 0 && !message.parent_message_id && (
            <button
              onClick={() => {
                setShowThread(!showThread);
                if (!showThread && onViewThread) {
                  onViewThread(message.id);
                }
              }}
              className={`flex items-center space-x-1 mt-1 text-xs text-blue-600 hover:text-blue-800 hover:underline ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
              <span>
                {message.thread_reply_count} {message.thread_reply_count === 1 ? 'reply' : 'replies'}
              </span>
              <svg className={`w-3 h-3 transition-transform ${showThread ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          )}

          {/* Thread replies inline display */}
          {showThread && threadReplies && threadReplies.replies && threadReplies.replies.length > 0 && (
            <div className="mt-2 pl-4 border-l-2 border-blue-200 space-y-2">
              {threadReplies.replies.map(reply => (
                <div key={reply.id} className="text-sm">
                  <div className="flex items-center space-x-1">
                    <span className="font-medium text-gray-700">{reply.sender?.display_name || 'Unknown'}</span>
                    <span className="text-[10px] text-gray-400">{formatTime(reply.created_at)}</span>
                  </div>
                  <p className="text-gray-800">{reply.content}</p>
                </div>
              ))}
              {threadReplies.loading && (
                <div className="text-xs text-gray-400">Loading...</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessageItem;
